import { useEffect, useRef, useState } from "react";

export interface CanvasBarDatum {
  key: string;
  value: number;
  label?: string;
  color?: string;
}

interface CanvasBarChartProps {
  data: CanvasBarDatum[];
  height?: number;
  layout?: "vertical" | "horizontal";
  defaultColor?: string;
  formatTooltip?: (d: CanvasBarDatum) => string;
  emptyLabel?: string;
}

const EASE_MS = 480;

function easeOutExpo(t: number) {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function readCssColor(el: HTMLElement, varExpr: string): string {
  if (!varExpr.startsWith("var(")) return varExpr;
  const name = varExpr.slice(4, -1).trim();
  const value = getComputedStyle(el).getPropertyValue(name).trim();
  return value || "#888";
}

/**
 * Canvas-rendered bar chart: own compositing layer, devicePixelRatio-aware,
 * tweens bar values on data change instead of snapping (rAF, exponential
 * ease-out). Recharts/SVG re-lays out the whole DOM per frame; this only
 * repaints pixels, so it stays smooth as the underlying dataset grows.
 */
export function CanvasBarChart({
  data,
  height = 220,
  layout = "vertical",
  defaultColor = "var(--primary)",
  formatTooltip,
  emptyLabel = "Keine Daten.",
}: CanvasBarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prevValuesRef = useRef<Map<string, number>>(new Map());
  const animStartRef = useRef(0);
  const rafRef = useRef<number | undefined>(undefined);
  const hoverIndexRef = useRef<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const reducedMotion = useRef(
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const containerEl = containerRef.current;
    if (!canvas || !containerEl) return;
    const container: HTMLDivElement = containerEl;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const targets = new Map(data.map((d) => [d.key, d.value]));
    const froms = new Map(data.map((d) => [d.key, prevValuesRef.current.get(d.key) ?? 0]));
    animStartRef.current = performance.now();

    const gridColor = readCssColor(container, "var(--border)");
    const mutedColor = readCssColor(container, "var(--muted-foreground)");
    const hoverFill = readCssColor(container, "var(--muted)");
    const fallbackFill = readCssColor(container, defaultColor);

    let dpr = Math.max(1, window.devicePixelRatio || 1);
    let width = 0;
    let cssHeight = height;

    function resize() {
      if (!container || !canvas) return;
      width = container.clientWidth;
      cssHeight = height;
      dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${cssHeight}px`;
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const padTop = 8;
    const padBottom = layout === "vertical" ? 20 : 8;
    const padLeft = layout === "horizontal" ? 8 : 8;
    const padRight = 8;
    const gap = layout === "vertical" ? 6 : 8;

    function bandRects() {
      const n = data.length;
      const plotW = width - padLeft - padRight;
      const plotH = cssHeight - padTop - padBottom;
      const maxVal = Math.max(1, ...data.map((d) => targets.get(d.key) ?? 0));
      return data.map((d, i) => {
        const bandW = layout === "vertical" ? (plotW - gap * (n - 1)) / n : plotW;
        const bandH = layout === "horizontal" ? (plotH - gap * (n - 1)) / n : plotH;
        const x = layout === "vertical" ? padLeft + i * (bandW + gap) : padLeft;
        const y = layout === "horizontal" ? padTop + i * (bandH + gap) : padTop;
        return { d, i, x, y, bandW, bandH, maxVal, plotW, plotH };
      });
    }

    function draw(now: number) {
      if (!ctx || !canvas) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, cssHeight);

      const elapsed = now - animStartRef.current;
      const t = reducedMotion.current ? 1 : Math.min(1, elapsed / EASE_MS);
      const eased = easeOutExpo(t);

      const rects = bandRects();
      const { maxVal, plotW, plotH } = rects[0] ?? { maxVal: 1, plotW: width, plotH: cssHeight };

      if (layout === "vertical") {
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 1;
        const gridLines = 3;
        for (let g = 0; g <= gridLines; g++) {
          const y = padTop + (plotH * g) / gridLines;
          ctx.beginPath();
          ctx.moveTo(padLeft, y);
          ctx.lineTo(width - padRight, y);
          ctx.stroke();
        }
      }

      rects.forEach((r) => {
        const from = froms.get(r.d.key) ?? 0;
        const to = targets.get(r.d.key) ?? 0;
        const value = from + (to - from) * eased;
        const ratio = value / maxVal;
        const fill = r.d.color ? readCssColor(container, r.d.color) : fallbackFill;
        const isHover = hoverIndexRef.current === r.i;

        ctx.fillStyle = isHover ? hoverFill : "transparent";
        if (layout === "vertical") {
          ctx.fillRect(r.x, padTop, r.bandW, plotH);
        } else {
          ctx.fillRect(padLeft, r.y, plotW, r.bandH);
        }

        ctx.fillStyle = fill;
        const radius = 4;
        if (layout === "vertical") {
          const barH = Math.max(0, plotH * ratio);
          const y = padTop + plotH - barH;
          roundRectTop(ctx, r.x, y, r.bandW, barH, radius);
        } else {
          const barW = Math.max(0, plotW * ratio);
          roundRectRight(ctx, padLeft, r.y, barW, r.bandH, radius);
        }
      });

      if (layout === "vertical") {
        ctx.fillStyle = mutedColor;
        ctx.font = "10px var(--font-mono, monospace)";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        // Sample the axis: draw only every nth label so ticks never collide,
        // measured against the widest label instead of assuming they fit.
        const first = rects[0];
        if (first) {
          const maxLabelW = Math.max(
            1,
            ...data.map((d) => (d.label ? ctx.measureText(d.label).width : 0)),
          );
          const step = Math.max(1, Math.ceil((maxLabelW + 12) / (first.bandW + gap)));
          rects.forEach((r) => {
            if (!r.d.label || r.i % step !== 0) return;
            ctx.fillText(r.d.label, r.x + r.bandW / 2, cssHeight - padBottom + 6);
          });
        }
      }

      if (t < 1) {
        rafRef.current = requestAnimationFrame(draw);
      }
    }

    rafRef.current = requestAnimationFrame(draw);
    prevValuesRef.current = targets;

    function requestRedraw() {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame((now) => {
        rafRef.current = undefined;
        draw(now);
      });
    }

    function handleMove(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const rects = bandRects();
      let found: number | null = null;
      for (const r of rects) {
        if (layout === "vertical") {
          if (mx >= r.x && mx <= r.x + r.bandW) found = r.i;
        } else if (my >= r.y && my <= r.y + r.bandH) {
          found = r.i;
        }
      }
      if (hoverIndexRef.current !== found) {
        hoverIndexRef.current = found;
        setHoverIndex(found);
        requestRedraw();
      }
      setTooltipPos(found !== null ? { x: mx, y: my } : null);
    }
    function handleLeave() {
      hoverIndexRef.current = null;
      setHoverIndex(null);
      setTooltipPos(null);
      requestRedraw();
    }
    canvas.addEventListener("mousemove", handleMove);
    canvas.addEventListener("mouseleave", handleLeave);

    return () => {
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      canvas.removeEventListener("mousemove", handleMove);
      canvas.removeEventListener("mouseleave", handleLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, height, layout]);

  if (data.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  const hovered = hoverIndex !== null ? data[hoverIndex] : null;

  const describe = (d: CanvasBarDatum) =>
    formatTooltip ? formatTooltip(d) : `${d.label ?? d.key}: ${d.value}`;
  const chartLabel = data.map(describe).join(", ");

  return (
    <div ref={containerRef} className="relative overflow-hidden" style={{ height }}>
      <canvas ref={canvasRef} className="block" role="img" aria-label={chartLabel} />
      {/* A <table> sizes its own generated box to fit the long caption/cell
          text no matter what width the sr-only utility specifies (Chrome
          ignores size containment on table boxes, and table-layout: fixed
          doesn't override it either), and that oversized-but-invisible
          absolutely-positioned box still inflates the page's scrollable
          area. `overflow-hidden` on this relative container clips it at the
          container boundary instead of fighting the table's own sizing. */}
      <table className="sr-only">
        <caption>{chartLabel}</caption>
        <thead>
          <tr>
            <th scope="col">Bezeichnung</th>
            <th scope="col">Wert</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.key}>
              <th scope="row">{d.label ?? d.key}</th>
              <td>{d.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hovered && tooltipPos && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-border bg-popover px-2 py-1 font-mono text-xs text-popover-foreground shadow-sm"
          style={{
            left: Math.min(tooltipPos.x + 10, (containerRef.current?.clientWidth ?? 0) - 90),
            top: Math.max(tooltipPos.y - 30, 0),
          }}
        >
          {formatTooltip ? formatTooltip(hovered) : `${hovered.label ?? hovered.key}: ${hovered.value}`}
        </div>
      )}
    </div>
  );
}

function roundRectTop(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h);
  ctx.beginPath();
  ctx.moveTo(x, y + h);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h);
  ctx.closePath();
  ctx.fill();
}

function roundRectRight(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w, h / 2);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x, y + h);
  ctx.closePath();
  ctx.fill();
}
